/**
 * On-device thread summaries for the Community — same model, same rules as the
 * AI notes feature (localAI.ts): Chrome's built-in Gemini Nano through the
 * Prompt API, with the Summarizer API as a second on-device route. Nothing
 * leaves the browser, no API key, no per-call cost. Where neither exists
 * (Safari, Firefox, mobile), every export degrades to "unavailable".
 */

type Availability = "unavailable" | "downloadable" | "downloading" | "available";

interface LanguageModelSession {
  prompt(input: string, opts?: { responseConstraint?: object; signal?: AbortSignal }): Promise<string>;
  destroy(): void;
}
interface LanguageModelStatic {
  availability(opts?: object): Promise<Availability>;
  create(opts?: {
    initialPrompts?: Array<{ role: "system" | "user" | "assistant"; content: string }>;
    monitor?: (m: { addEventListener: (e: string, cb: (ev: any) => void) => void }) => void;
    signal?: AbortSignal;
  }): Promise<LanguageModelSession>;
}
interface SummarizerSession {
  summarize(input: string, opts?: { context?: string; signal?: AbortSignal }): Promise<string>;
  destroy(): void;
}
interface SummarizerStatic {
  availability(opts?: object): Promise<Availability>;
  create(opts?: {
    type?: "tldr" | "key-points" | "teaser" | "headline";
    format?: "plain-text" | "markdown";
    length?: "short" | "medium" | "long";
    sharedContext?: string;
    monitor?: (m: { addEventListener: (e: string, cb: (ev: any) => void) => void }) => void;
    signal?: AbortSignal;
  }): Promise<SummarizerSession>;
}

const languageModel = (): LanguageModelStatic | null => {
  if (typeof window === "undefined") return null;
  const lm = (globalThis as any).LanguageModel;
  return lm && typeof lm.availability === "function" ? lm : null;
};
const summarizerApi = (): SummarizerStatic | null => {
  if (typeof window === "undefined") return null;
  const sm = (globalThis as any).Summarizer;
  return sm && typeof sm.availability === "function" ? sm : null;
};

export type SummaryEngine = "prompt-api" | "summarizer";

export async function threadSummaryAvailability(): Promise<{ engine: SummaryEngine | null; state: Availability }> {
  for (const [engine, api] of [
    ["prompt-api", languageModel()],
    ["summarizer", summarizerApi()],
  ] as Array<[SummaryEngine, { availability(): Promise<Availability> } | null]>) {
    if (!api) continue;
    try {
      const state = await api.availability();
      if (state !== "unavailable") return { engine, state };
    } catch {
      /* enterprise policy can throw — treat as absent */
    }
  }
  return { engine: null, state: "unavailable" };
}

export interface ThreadSummaryInput {
  title: string;
  body: string;
  replies: Array<{ author: string; text: string }>;
}
export interface ThreadSummaryResult {
  summary: string;
  takeaways: string[];
  engine: SummaryEngine;
}
export interface SummaryProgress {
  stage: "checking" | "downloading" | "running";
  pct?: number;
}

const SCHEMA = {
  type: "object",
  properties: {
    summary: { type: "string", description: "2-3 plain sentences: what the thread is about and where it landed" },
    takeaways: { type: "array", items: { type: "string" }, description: "At most 4 concrete points an agent can use: carriers, numbers, decisions, warnings" },
  },
  required: ["summary", "takeaways"],
  additionalProperties: false,
} as const;

const SYSTEM = [
  "You summarize a forum thread between licensed life-insurance agents for another agent who has not read it.",
  "Write plainly. Use only what is in the thread. Copy every number, carrier name and condition exactly as written; never invent or round.",
  "Do not name any client, applicant or insured even if the thread does. Agents' names are fine.",
  "If the thread has no answers yet, say so in one sentence and keep takeaways empty.",
].join(" ");

const CHUNK_CHARS = 6_000;

function transcript(input: ThreadSummaryInput): string {
  const head = `THREAD: ${input.title}\n\nPOST: ${input.body}`;
  const replies = input.replies.map((r, i) => `\nREPLY ${i + 1} (${r.author}): ${r.text}`).join("\n");
  return `${head}\n${replies}`.trim();
}

function chunk(text: string): string[] {
  if (text.length <= CHUNK_CHARS) return [text];
  const out: string[] = [];
  for (let i = 0; i < text.length; i += CHUNK_CHARS) out.push(text.slice(i, i + CHUNK_CHARS));
  return out;
}

const monitorDownload = (onProgress?: (p: SummaryProgress) => void) => (m: { addEventListener: (e: string, cb: (ev: any) => void) => void }) => {
  m.addEventListener("downloadprogress", (e: any) => {
    const pct = typeof e.loaded === "number" ? Math.round(e.loaded * 100) : undefined;
    onProgress?.({ stage: "downloading", pct });
  });
};

function parseJson(raw: string): { summary: string; takeaways: string[] } | null {
  try {
    const match = raw.match(/\{[\s\S]*\}/);
    const parsed = JSON.parse(match ? match[0] : raw);
    return {
      summary: String(parsed.summary || "").trim(),
      takeaways: Array.isArray(parsed.takeaways) ? parsed.takeaways.map((t: unknown) => String(t).trim()).filter(Boolean).slice(0, 4) : [],
    };
  } catch {
    return null;
  }
}

// Sessions opened synchronously INSIDE the click handler. Chrome requires a
// user gesture to create a session while the model is "downloadable" /
// "downloading", and transient activation does not survive an await — the
// same trap AI notes hit (localAI.ts, 2026-08-21). Call this as the first
// statement of the handler and pass the result to summarizeThread().
export interface PendingSummarySessions {
  engine: SummaryEngine;
  prompt?: Promise<LanguageModelSession>;
  tldr?: Promise<SummarizerSession>;
  points?: Promise<SummarizerSession>;
}

const SHARED_CONTEXT = "A public forum thread between licensed life-insurance agents. Keep numbers and carrier names exact. Never name a client.";

export function beginThreadSummarySessions(engine: SummaryEngine, onProgress?: (p: SummaryProgress) => void, signal?: AbortSignal): PendingSummarySessions {
  if (engine === "prompt-api") {
    return {
      engine,
      prompt: languageModel()!.create({ initialPrompts: [{ role: "system", content: SYSTEM }], monitor: monitorDownload(onProgress), signal }),
    };
  }
  const api = summarizerApi()!;
  return {
    engine,
    tldr: api.create({ type: "tldr", format: "plain-text", length: "short", sharedContext: SHARED_CONTEXT, monitor: monitorDownload(onProgress), signal }),
    points: api.create({ type: "key-points", format: "plain-text", length: "medium", sharedContext: SHARED_CONTEXT, signal }),
  };
}

export async function summarizeThread(
  input: ThreadSummaryInput,
  sessions: PendingSummarySessions,
  onProgress?: (p: SummaryProgress) => void,
  signal?: AbortSignal,
): Promise<ThreadSummaryResult> {
  const engine = sessions.engine;
  const text = transcript(input);

  if (engine === "prompt-api") {
    const session = await sessions.prompt!;
    try {
      onProgress?.({ stage: "running" });
      const parts = chunk(text);
      const partials: Array<{ summary: string; takeaways: string[] }> = [];
      for (const part of parts) {
        const raw = await session.prompt(`Summarize this thread.\n\n${part}`, { responseConstraint: SCHEMA, signal });
        const parsed = parseJson(raw);
        if (parsed) partials.push(parsed);
      }
      if (partials.length === 0) throw new Error("empty");
      if (partials.length === 1) return { ...partials[0], engine };
      const merged = await session.prompt(
        `Merge these partial summaries of one long thread into a single summary.\n\n${partials
          .map((p, i) => `PART ${i + 1}: ${p.summary}\nPOINTS: ${p.takeaways.join(" | ")}`)
          .join("\n\n")}`,
        { responseConstraint: SCHEMA, signal },
      );
      return { ...(parseJson(merged) || partials[0]), engine };
    } finally {
      session.destroy();
    }
  }

  // Summarizer API: two on-device passes, a tl;dr and the key points.
  const tldr = await sessions.tldr!;
  try {
    onProgress?.({ stage: "running" });
    const summary = (await tldr.summarize(text.slice(0, CHUNK_CHARS * 2), { signal })).trim();
    const points = await sessions.points!;
    try {
      const raw = await points.summarize(text.slice(0, CHUNK_CHARS * 2), { signal });
      const takeaways = raw
        .split(/\n+/)
        .map((l) => l.replace(/^[\s\-*•\d.)]+/, "").trim())
        .filter(Boolean)
        .slice(0, 4);
      return { summary, takeaways, engine };
    } finally {
      points.destroy();
    }
  } finally {
    tldr.destroy();
  }
}
